import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Menu, Plus, User } from "lucide-react";

export default function Nav() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-40 w-full backdrop-blur-xl bg-white/70 border-b border-neutral-200"
      data-testid="site-nav"
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 lg:px-10 h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <div className="w-7 h-7 rounded-sm bg-[#FF4500] grid place-items-center">
            <span className="text-white font-display font-black text-sm">S</span>
          </div>
          <span className="font-display font-black tracking-tighter text-xl">SpareHours</span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          <Link to="/search" className="hover:text-[#FF4500] transition-colors" data-testid="nav-explore">Find a Space</Link>
          <Link to="/how-it-works" className="hover:text-[#FF4500] transition-colors" data-testid="nav-how">How it works</Link>
          <Link to="/list-your-space" className="text-[#FF4500] font-semibold hover:text-[#E63E00] transition-colors" data-testid="nav-host">List a Space</Link>
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full border border-neutral-200 pl-3 pr-1 py-1 hover:shadow-[0_4px_16px_rgba(0,0,0,0.06)] transition-shadow"
                  data-testid="nav-user-menu"
                >
                  <Menu className="w-4 h-4" />
                  <div className="w-8 h-8 rounded-full bg-[#0A0A0A] text-white grid place-items-center text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5 text-xs text-neutral-500 truncate">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/trips")} data-testid="menu-trips">My trips</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/host")} data-testid="menu-host">Host dashboard</DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin control room</DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/host/new")} data-testid="menu-list-space">
                  <Plus className="w-4 h-4 mr-2" /> List a space
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => { logout(); navigate("/"); }}
                  data-testid="menu-logout"
                >
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <>
              <Button variant="ghost" size="sm" onClick={() => navigate("/login")} data-testid="nav-login">
                Log in
              </Button>
              <Button
                size="sm"
                className="bg-[#FF4500] hover:bg-[#E63E00] text-white rounded-full"
                onClick={() => navigate("/signup")}
                data-testid="nav-signup"
              >
                Sign up
              </Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
